// How the root stack's screens are grouped on wide layouts
// (components/navigation/AdaptiveStack.js). Phones never group.

// Screens of one event. Consecutive ones share one card: the event menu on the
// left, the open screen on the right.
export const EVENT_ROUTES = new Set([
  "EventEdit",
  "AddRoom",
  "EventDetails",
  "ReuploadVideo",
  "HowToStream",
  "EventDonations",
  "EventPrivacy",
  "EventBranding",
  "EventGuestbook",
  "Embed",
  "GuestList",
  "GuestAdd",
  "GuestImport",
  "EventAnalytics",
  "SellTickets",
  "PayWall",
  "PurchaseSuccess",
  "Test",
  "EventPeople",
]);

// Event screens that take the whole event frame (no menu), like checkout.
export const EVENT_FULL_FRAME_ROUTES = new Set([
  "PayWall",
  "PurchaseSuccess",
  "Test",
]);

// Account screens opened over the dashboard. In the modal layout they show in a
// centred frame like the event; when opened from an event (Plans from checkout,
// Update Email from Sell Tickets) they stay inside the event's frame.
export const SHEET_ROUTES = new Set([
  "CreateEvent",
  "Profile",
  "Plans",
  "VideoWall",
  "WhatsNew",
  "ViewerHours",
  "DownloadEverything",
  "UpdateEmail",
  "ForgotPassword",
  "ForgotPasswordLinkSent",
  "Team",
]);

// An account that keeps one test event (no prefer_multiple_tests) only ever
// gets that test's How to Go Live, the way phones show it: in a frame of its
// own, over the event it was started from, without the event menu. It's a
// separate practice event, not something to edit, upload video to or delete.
// Accounts with several tests manage them like events.
export const isSingleTest = (event, { multipleTests = false } = {}) =>
  !!event?.is_test && !multipleTests;

// Everything else (dashboard, Go Live and browser studios, review prompt,
// password reset, onboarding) keeps its own full-screen card.
export const getRouteGroup = (route, options) => {
  const name = route?.name;
  if (name === "HowToStream" && isSingleTest(route.params?.event, options)) {
    return "test";
  }
  return EVENT_ROUTES.has(name)
    ? "event"
    : SHEET_ROUTES.has(name)
    ? "sheet"
    : null;
};

// [{ type, routes, key }]: runs of consecutive routes of the same group (type
// null for routes that keep their own card). A group's card key names the
// screen it opens over, not its own first route, so replacing that route
// (switching to another room of the event) keeps the card mounted.
export const groupRuns = (routes, getGroup = getRouteGroup) => {
  const runs = [];
  for (const route of routes) {
    const last = runs[runs.length - 1];
    let type = getGroup(route);
    // An account screen opened from an event stays in the event's card.
    if (type === "sheet" && last?.type === "event") type = "event";
    if (type && last?.type === type) {
      last.routes.push(route);
    } else {
      const below = last?.routes[last.routes.length - 1];
      runs.push({
        type,
        routes: [route],
        key: type ? `group-${type}-${below ? below.key : "root"}` : null,
      });
    }
  }
  return runs;
};

// The menu entry a screen belongs to, for highlighting it in the event menu.
const SECTION_OF = {
  GuestAdd: "GuestList",
  GuestImport: "GuestList",
};

export const sectionOf = (routeName) => SECTION_OF[routeName] || routeName;

export const slugOf = (route) =>
  route?.params?.slug ||
  route?.params?.event?.slug ||
  route?.params?.relatedEvent?.slug ||
  null;
